/**
 * Azure Sync Service
 *
 * API client for Azure Table Storage sync endpoints.
 * Part of UC-017: Azure Table Storage Sync for GDPR Admin
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

/**
 * Get sync status statistics
 * @returns {Promise<{azure_table_total, snowflake_total, synced_count, orphaned_count, missing_count}>}
 */
export const getSyncStatus = async () => {
  const response = await fetch(`${API_URL}/gdpradm/azure-sync/status`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to get sync status');
  }

  return response.json();
};

/**
 * List Azure Table users with sync status
 * @param {Object} options - Query options
 * @param {string} options.filter - Filter: 'orphaned', 'synced', or 'all'
 * @param {string} options.search - Search by email
 * @param {number} options.limit - Max results (default 50)
 * @param {number} options.offset - Pagination offset
 * @returns {Promise<{users: Array, total: number, orphaned_count: number}>}
 */
export const listAzureUsers = async ({ filter, search, limit = 50, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (filter) params.append('filter', filter);
  if (search) params.append('search', search);
  params.append('limit', limit);
  params.append('offset', offset);

  const response = await fetch(`${API_URL}/gdpradm/azure-sync/users?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to list Azure users');
  }

  return response.json();
};

/**
 * Delete orphan user from Azure Table only
 * @param {string} email - User email to delete
 * @returns {Promise<{success: boolean, message: string, email: string}>}
 */
export const deleteAzureUser = async (email) => {
  const response = await fetch(`${API_URL}/gdpradm/azure-sync/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to delete Azure user');
  }

  return response.json();
};

/**
 * Sync user from Azure Table to Snowflake
 * @param {string} email - User email to sync
 * @returns {Promise<{success: boolean, message: string, email: string, user_id: string}>}
 */
export const syncUserToSnowflake = async (email) => {
  const response = await fetch(`${API_URL}/gdpradm/azure-sync/users/${encodeURIComponent(email)}/sync`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Failed to sync user to Snowflake');
  }

  return response.json();
};

export default {
  getSyncStatus,
  listAzureUsers,
  deleteAzureUser,
  syncUserToSnowflake,
};
